<template lang="html">
    <scroll-container
        class="report"
        @change="scrolled"
    >
        <div
            class="report-layout"
        >
            <table-of-contents
                class="is-hidden-mobile"
                :title="tocTitle"
                :search-bar="tocSearch"
                :sections="activeSections"
                :filtered-sections="activeFilteredSections"
                :active-id="activeId"
                @typing="onTocSearch"
            >
                <template
                    #options
                >
                    <b-button
                        v-for="button in buttons"
                        :key="button.label"
                        expanded
                        :loading="button.loading"
                        icon-left="download"
                        @click="$emit(button.event)"
                    >
                        {{ button.label }}
                    </b-button>
                </template>
            </table-of-contents>
            <div
                class="report-main report-content"
            >
                <div class="report-header">
                    <div class="report-titles">
                        <h1
                            v-if="title"
                            class="title is-1"
                            v-text="title"
                        />
                        <h2
                            v-if="subtitle"
                            class="subtitle is-2"
                            v-text="subtitle"
                        />
                        <slot name="additional-subtitles" />
                    </div>
                    <slot name="header-content" />
                </div>

                <hr>

                <component
                    :is="section.sectionComponent"
                    v-for="section in tocSearch ? activeFilteredSections: activeSections"
                    :id="section.id"
                    :key="section.id"
                    :report-data="reportData"
                />

                <div class="report-bottom">
                    <b-button
                        tag="a"
                        href="#map"
                    >
                        Back to map
                    </b-button>
                </div>
            </div>
        </div>
    </scroll-container>
</template>

<script>
import TableOfContents from '@foundryspatial/table-of-contents';

export default {
    name: 'Report',

    components: {
        TableOfContents,
    },

    props: {
        /**
         * The title of the report.
         */
        title: {
            type: String,
            required: true,
        },

        /**
         * The subtitle of the report.
         */
        subtitle: {
            type: String,
            required: false,
            default: null,
        },

        /**
         * All data needed to required by the report. This object is passed directly to the report sections.
         */
        reportData: {
            type: Object,
            required: false,
            default: () => {},
        },

        /**
         * The sections to create the report from. This array is expected to be in the following format:
         *
         *      [
         *          {
         *              title: 'Overview',
         *              id: 'overview',
         *              sectionComponent: OverviewSection,
         *          },
         *          {
         *              title: 'Introduction',
         *              id: 'introduction',
         *              sectionComponent: IntroductionSection,
         *          },
         *          [...]
         *      ]
         *
         * The sectionComponent is expected to be a Vue component.
         */
        sections: {
            type: Array,
            required: false,
            default: () => [],
        },

        /**
         * The filtered sections have similar format to sections and are used to create the dynamic filtered report.
         */
        filteredSections: {
            type: Array,
            required: false,
            default: () => ([]),
        },

        /**
         * The sections to remove from the report. This is expected to be an array of section id strings.
         */
        removedSections: {
            type: Array,
            required: false,
            default: () => [],
        },

        /**
         * The buttons to be displayed in the ToC. This array is expected to be in the following format:
         *
         *      [
         *          {
         *              label: 'Download PDF',
         *              event: 'download-pdf',
         *              loading: this.pdfLoading,
         *          },
         *          [...]
         *      ]
         */
        buttons: {
            type: Array,
            required: false,
            default: () => [],
        },

        /**
         * Title of table of contents.
         */
        tocTitle: {
            type: String,
            default: 'Table of Contents',
        },

        /**
         * Search bar of table of contents.
         */
        tocSearch: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            activeId: null,
        };
    },

    computed: {
        /**
         * All sections that should currently be displayed to the user.
         */
        activeSections() {
            return this.sections.filter((section) => this.removedSections.indexOf(section.id) === -1);
        },

        /**
         * All dynamic filtered sections that should currently be displayed to the user.
         */
        activeFilteredSections() {
            return this.filteredSections.filter((section) => this.removedSections.indexOf(section.id) === -1);
        },
    },

    beforeDestroy() {
        // remove the event listener so it doesn't keep adding one via mounted()
        window.removeEventListener('hashchange', this.onHashChange);
    },

    mounted() {
        window.addEventListener('hashchange', this.onHashChange);
    },

    methods: {
        /**
         * Highlight a specific section depending on where you are in the screen.
         * @param {Object} scrollEvent An object detailing various scrollItems in the scroll monitor.
         */
        scrolled(scrollEvent) {
            const visibleSection = this.sections.find((section) => (
                section.id in scrollEvent && scrollEvent[section.id].isFullyInViewport
            ));
            if (visibleSection) this.activeId = visibleSection.id;

            if (this.activeId) {
                window.history.replaceState(null, '', `#${this.activeId}`);
            }
        },

        /**
         * When a hash changes in the URL, set the activeId for the report.
         * @param {Object} e hash change event object
         */
        onHashChange(e) {
            const activeSection = this.sections.find((section) => window.location.hash === `#${section.id}`);
            if (activeSection) this.activeId = activeSection.id;

            e.preventDefault();
            e.stopPropagation();
        },

        /**
         * Search table of contents filtered sections and emit "search" event with filtered sections list.
         * @param {String} query search term string
         * @param {Array} sections searched sections list
         */
        onTocSearch(query, sections) {
            this.$emit('search', query, sections);
        },
    },
};
</script>

<style lang="scss">
.report-layout {
    $primary: #33284a !default;
    $section-divider-color: #e6e6e6 !default;
    $section-margin-bottom: 2.5rem !default;
    $fullhd: 1408px !default;
    $desktop: 1024px !default;
    $tablet: 769px !default;

    display: grid;
    grid-template-columns: 1fr 5vw minmax(0, 5fr);
    position: relative;
    width: 95vw;

    @media screen and (min-width: $fullhd) {
        width: 85vw;
    }

    @media screen and (max-width: $tablet) {
        grid-template-columns: 5vw 90vw 5vw;
        width: 100vw;
    }

    .report-main {
        grid-column: 3/5;
        margin-top: 2em;
        width: 100%;

        @media screen and (max-width: $tablet) {
            grid-column: 2/3;
        }

        .title,
        .title a,
        .subtitle,
        .subtitle a {
            color: $primary;
        }

        p {
            margin: 1em 0;

            &.no-top-margin {
                margin-top: 0;
            }

            &.no-btm-margin {
                margin-bottom: 0;
            }

            &.extra-btm-margin {
                margin-bottom: 1.5em;
            }
        }

        // footer elements
        .footer {
            padding: 2rem 1.5rem 2rem;
            font-size: 0.7em;

            p:last-child {
                margin: 0;
            }
        }

        hr {
            margin-bottom: $section-margin-bottom;
        }

        .report-header {
            display: flex;
            flex-direction: row;

            @media screen and (max-width: $desktop) {
                flex-direction: column;
                height: fit-content;
            }

            .report-titles {
                flex: 1;
            }
        }

        .report-bottom {
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 3rem 0;
        }
    }
}
</style>
